import { type Client, clientSingleton } from '@edge/spark/io-apollo'
import { type AllOrganizationsQuery, AllOrganizationsDocument, useAllOrganizationsQuery } from '../generated/graphql'
import { getActiveAuctionId } from '../activeAuctionIdStore'
import { filterMaybe } from '@edge/spark/utils'

const pickOrganizations = (data?: AllOrganizationsQuery) => {
    const orgs = data?.user?.organizations?.filter(filterMaybe) || []
    return orgs
}

const pickActiveOrganization = (data?: AllOrganizationsQuery) => {
    const activeOrgId = getActiveAuctionId()
    const activeOrg = pickOrganizations(data).find((o) => o?.orgId === activeOrgId)
    return activeOrg
}

const pickActiveOrganizationType = (data?: AllOrganizationsQuery) => {
    const activeOrgId = getActiveAuctionId()
    const activeOrgType = pickOrganizations(data).find((o) => o?.orgId === activeOrgId)?.type
    return activeOrgType
}

export const useActiveOrganization = (...props: Parameters<typeof useAllOrganizationsQuery>) => {
    const [firstProp, ...rest] = props
    const { data: allOrgsData } = useAllOrganizationsQuery({ fetchPolicy: 'cache-first', ...firstProp }, ...rest)
    const activeOrg = pickActiveOrganization(allOrgsData)
    return activeOrg
}

export const useActiveOrganizationType = (...props: Parameters<typeof useActiveOrganization>) => {
    const [firstProp, ...rest] = props
    const { data: allOrgsData } = useAllOrganizationsQuery({ fetchPolicy: 'cache-first', ...firstProp }, ...rest)
    const activeOrgType = pickActiveOrganizationType(allOrgsData)
    return activeOrgType
}

interface GetActiveOrgProps {
    /** ApolloClient. Attempts to use client singleton if not passed */
    client?: Client
}

/** Uses passed client or clientSingleton for `AllOrganizationsQuery` */
export const getActiveOrganization = async (props?: GetActiveOrgProps) => {
    const { client: propsClient } = props || {}
    const client = propsClient || clientSingleton
    const { data } = await client.query<AllOrganizationsQuery>({
        query: AllOrganizationsDocument,
        fetchPolicy: 'cache-first',
    })
    const activeOrg = pickActiveOrganization(data)
    return activeOrg
}
